package com.arena_web.ui.sections.dashboard

import androidx.compose.runtime.*
import com.arena_web.ui.components.charts.OccupancyLevel
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.TimeSwitcher
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// =============================================================================
// Shared chart card wrapper — Figma: two-layer, 40px radius, backdrop blur
// =============================================================================

@Composable
private fun ChartCard(
    title: String,
    modifier: Modifier = Modifier,
    paddingBottom: Int = 18,
    selectedPeriod: String = "WEEK",
    onPeriodChange: (String) -> Unit = {},
    content: @Composable () -> Unit
) {
    // Outer wrapper — Revenue Block / Bookings Block layer
    Div(attrs = {
        style {
            width(100.percent)
            height(100.percent)
            property("box-sizing", "border-box")
            property("border-radius", s(40))
            property("overflow", "clip")
            property("backdrop-filter", "blur(${s(11)})")
            property("-webkit-backdrop-filter", "blur(${s(11)})")
            property("background",
                "linear-gradient(176deg, rgba(60, 121, 241, 0.135) 13.94%, rgba(5, 4, 12, 0.03) 69.49%)")
        }
    }) {
        // Inner Block_Goals layer — Figma: justify-between, pt-16 pb-18 px-16
        Column(
            modifier = modifier
                .fillMaxSize()
                .sBorderRadius(40)
                .styleModifier {
                    property("box-sizing", "border-box")
                    property("padding", "${s(16)} ${s(16)} ${s(paddingBottom)} ${s(16)}")
                    property("justify-content", "space-between")
                    property("background",
                        "linear-gradient(18.7deg, rgba(0, 59, 174, 0.03) 26.9%, rgba(3, 87, 230, 0.03) 67.5%)")
                    property("border", "1px solid rgba(41, 52, 138, 0.2)")
                    property("overflow", "clip")
                }
        ) {
            // Header section: headline + time switcher — Figma: gap-8
            Column(
                modifier = Modifier.fillMaxWidth().sGap(8)
            ) {
                // Headline row — Figma: 48px tall, pl-8
                Row(
                    modifier = Modifier.fillMaxWidth().sHeight(48).sPaddingLeft(8),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        title,
                        modifier = Modifier
                            .flexGrow(1)
                            .sFontSize(24)
                            .fontWeight(AppTypography.Medium)
                            .color(AppColors.Text.Secondary)
                    )
                }

                TimeSwitcher(
                    activePeriodKey = selectedPeriod,
                    onPeriodChanged = { onPeriodChange(it.uppercase()) }
                )
            }

            // Chart content — fills remaining height
            Div(attrs = {
                style {
                    property("flex", "1 1 0px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("min-height", "0")
                    width(100.percent)
                }
            }) {
                content()
            }
        }
    }
}

// =============================================================================
// SVG helpers
// =============================================================================

private fun smoothPath(pts: List<Pair<Double, Double>>): String {
    if (pts.size < 2) return "M ${pts.first().first} ${pts.first().second}"
    val sb = StringBuilder("M ${pts.first().first} ${pts.first().second}")
    for (i in 1 until pts.size) {
        val prev = pts[i - 1]
        val curr = pts[i]
        val tension = 0.3
        val dx = curr.first - prev.first
        val cp1x = prev.first + dx * tension
        val cp1y = prev.second
        val cp2x = curr.first - dx * tension
        val cp2y = curr.second
        sb.append(" C $cp1x $cp1y, $cp2x $cp2y, ${curr.first} ${curr.second}")
    }
    return sb.toString()
}

private fun buildAreaChartSvg(
    data: List<Double>,
    lineColorHex: String,
    fillR: Int, fillG: Int, fillB: Int,
    idPrefix: String = "chart"
): String {
    val maxValue = data.max()
    val normPoints = data.mapIndexed { idx, value ->
        val xPct = if (data.size > 1) idx.toDouble() / (data.size - 1) * 100 else 50.0
        val yPct = 100.0 - (value / maxValue * 100)
        Pair(xPct, yPct)
    }

    val linePath = smoothPath(normPoints)
    val areaPath = "$linePath L ${normPoints.last().first} 100 L ${normPoints.first().first} 100 Z"
    val fillId = "${idPrefix}-fill"
    val clipId = "${idPrefix}-clip"

    return buildString {
        append("<svg width=\"100%\" height=\"100%\" viewBox=\"0 0 100 100\" preserveAspectRatio=\"none\" xmlns=\"http://www.w3.org/2000/svg\">")
        append("<defs>")
        append("<linearGradient id=\"$fillId\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\">")
        append("<stop offset=\"0%\" stop-color=\"rgba($fillR,$fillG,$fillB,0.65)\"/>")
        append("<stop offset=\"100%\" stop-color=\"rgba($fillR,$fillG,$fillB,0.03)\"/>")
        append("</linearGradient>")
        append("<clipPath id=\"$clipId\"><rect x=\"0\" y=\"0\" width=\"0\" height=\"100\"><animate attributeName=\"width\" from=\"0\" to=\"100\" dur=\"1s\" fill=\"freeze\" calcMode=\"spline\" keySplines=\"0.25 0.1 0.25 1\"/></rect></clipPath>")
        append("</defs>")
        append("<g clip-path=\"url(#$clipId)\">")
        append("<path d=\"$areaPath\" fill=\"url(#$fillId)\"/>")
        append("<path d=\"$linePath\" fill=\"none\" stroke=\"$lineColorHex\" stroke-width=\"5\" vector-effect=\"non-scaling-stroke\"/>")
        append("</g>")
        append("</svg>")
    }
}

// =============================================================================
// Area chart — Figma: chart 135px, axis dots 10px, labels 14px
// =============================================================================

@Composable
private fun AreaChart(
    data: List<Double>,
    labels: List<String>,
    lineColorHex: String,
    fillR: Int, fillG: Int, fillB: Int,
    dotGradientSvg: String,
    idPrefix: String = "chart",
    tooltipPrefix: String = "",
    tooltipSuffix: String = ""
) {
    val svgContent = buildAreaChartSvg(data, lineColorHex, fillR, fillG, fillB, idPrefix)
    var hoveredIndex by remember { mutableStateOf(-1) }
    val maxValue = data.max()
    val normPoints = data.mapIndexed { idx, value ->
        val xPct = if (data.size > 1) idx.toDouble() / (data.size - 1) * 100 else 50.0
        val yPct = 100.0 - (value / maxValue * 100)
        Pair(xPct, yPct)
    }

    Column(
        modifier = Modifier.fillMaxSize()
            .styleModifier { property("justify-content", "flex-end") }
    ) {
        // Chart SVG area — Figma: 135px height
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(142)
                .styleModifier { property("position", "relative") }
        ) {
            key(svgContent) {
                Div(
                    attrs = {
                        ref { el -> el.innerHTML = svgContent; onDispose { } }
                        style {
                            property("position", "absolute")
                            property("inset", "0")
                        }
                    }
                )
            }

            // Hover zones + data points + tooltips
            normPoints.forEachIndexed { idx, (xPct, yPct) ->
                val value = data[idx].toInt()
                // Hover zone
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("position", "absolute")
                            property("left", "$xPct%")
                            property("top", "0")
                            property("bottom", "0")
                            property("width", s(30))
                            property("transform", "translateX(-50%)")
                            property("cursor", "pointer")
                            property("z-index", "2")
                        }
                        .onMouseEnter { hoveredIndex = idx }
                        .onMouseLeave { hoveredIndex = -1 }
                )

                if (hoveredIndex == idx) {
                    // Dashed vertical line
                    Box(
                        modifier = Modifier.styleModifier {
                            property("position", "absolute")
                            property("left", "$xPct%")
                            property("top", "$yPct%")
                            property("bottom", "0")
                            property("width", "1px")
                            property("transform", "translateX(-50%)")
                            property("border-left", "1px dashed rgba(255,255,255,0.4)")
                            property("pointer-events", "none")
                            property("z-index", "1")
                        }
                    )

                    // Data point — Figma: 28x28 outer with radial gradient + mix-blend-screen, 14x14 inner #b4ccf6
                    Box(
                        modifier = Modifier
                            .sSize(28)
                            .sBorderRadius(100)
                            .styleModifier {
                                property("position", "absolute")
                                property("left", "$xPct%")
                                property("top", "$yPct%")
                                property("transform", "translate(-50%, -50%)")
                                property("pointer-events", "none")
                                property("z-index", "3")
                                property("overflow", "clip")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        // Radial gradient overlay with mix-blend-screen
                        Div(attrs = {
                            ref { el ->
                                el.innerHTML = dotGradientSvg
                                onDispose { }
                            }
                            style {
                                property("position", "absolute")
                                property("inset", "0")
                                property("mix-blend-mode", "screen")
                                property("pointer-events", "none")
                                property("border-radius", "100px")
                            }
                        })
                        // Inner solid dot
                        Box(
                            modifier = Modifier
                                .sSize(14)
                                .sBorderRadius(100)
                                .backgroundColor(AppColors.Text.Primary)
                        )
                    }

                    // Tooltip badge — Figma: rounded-28, bg #112140, pointer triangle
                    Box(
                        modifier = Modifier
                            .styleModifier {
                                property("position", "absolute")
                                property("left", "$xPct%")
                                property("top", "$yPct%")
                                property("transform", "translate(-50%, calc(-100% - ${s(20)}))")
                                property("z-index", "4")
                                property("pointer-events", "none")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            modifier = Modifier.styleModifier {
                                property("align-items", "center")
                            }
                        ) {
                            // Tooltip pill
                            Box(
                                modifier = Modifier
                                    .sBorderRadius(28)
                                    .sPadding(4, 12)
                                    .styleModifier {
                                        property("background", "#112140")
                                        property("white-space", "nowrap")
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    "$tooltipPrefix$value$tooltipSuffix",
                                    modifier = Modifier
                                        .sFontSize(16)
                                        .fontWeight(AppTypography.Regular)
                                        .color(AppColors.Text.Secondary)
                                )
                            }
                            // Pointer triangle
                            Div(attrs = {
                                style {
                                    property("width", "0")
                                    property("height", "0")
                                    property("border-left", "${s(4)} solid transparent")
                                    property("border-right", "${s(4)} solid transparent")
                                    property("border-top", "${s(6)} solid #112140")
                                    property("margin-top", "-1px")
                                }
                            })
                        }
                    }
                }
            }
        }

        // Decorative dotted X-axis — Figma: 10px height
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(10)
                .styleModifier { property("position", "relative") }
        ) {
            normPoints.forEachIndexed { idx, (xPct, _) ->
                // Large dot at data point position
                Box(
                    modifier = Modifier
                        .sSize(10)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("position", "absolute")
                            property("left", "$xPct%")
                            property("top", "50%")
                            property("transform", "translate(-50%, -50%)")
                            property("background", "rgba($fillR,$fillG,$fillB,0.6)")
                        }
                )
                // Small dots between this point and the next
                if (idx < normPoints.size - 1) {
                    val nextXPct = normPoints[idx + 1].first
                    for (j in 1..5) {
                        val pct = xPct + (nextXPct - xPct) * j / 6
                        Box(
                            modifier = Modifier
                                .sSize(4)
                                .sBorderRadius(100)
                                .styleModifier {
                                    property("position", "absolute")
                                    property("left", "$pct%")
                                    property("top", "50%")
                                    property("transform", "translate(-50%, -50%)")
                                    property("background", "rgba($fillR,$fillG,$fillB,0.3)")
                                }
                        )
                    }
                }
            }
        }

        // Gap 10px between axis dots and labels (Figma)
        Box(modifier = Modifier.sHeight(10))

        // X-axis labels — Figma: 14px height
        Row(
            modifier = Modifier.fillMaxWidth().styleModifier {
                property("justify-content", "space-between")
            }
        ) {
            labels.forEach { label ->
                SpanText(
                    label,
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(AppTypography.Regular)
                        .color(AppColors.Text.Secondary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }
    }
}

// =============================================================================
// Donut chart — Figma: pie 173x173, legend below
// =============================================================================

private data class DonutSegment(
    val label: String,
    val count: Int,
    val color: String
)

@Composable
private fun DonutChart(segments: List<DonutSegment>) {
    val t = LocalAppStrings.current
    val total = segments.sumOf { it.count }
    val circumference = 2.0 * 3.14159 * 40 // r=40

    // Figma: Booking Status Chart Container — flex-1, justify-between, items-start
    Column(
        modifier = Modifier.fillMaxSize()
            .styleModifier {
                property("justify-content", "space-between")
                property("align-items", "flex-start")
            }
    ) {
        // Donut SVG — Figma: 173x173, centered horizontally
        Box(
            modifier = Modifier.fillMaxWidth().styleModifier {
                property("display", "flex")
                property("justify-content", "center")
            }
        ) {
            Box(
                modifier = Modifier.sSize(173).styleModifier { property("position", "relative") },
                contentAlignment = Alignment.Center
            ) {
                val svgContent = buildString {
                    append("<svg width=\"100%\" height=\"100%\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\">")
                    var offset = 0.0
                    segments.forEach { seg ->
                        val pct = seg.count.toDouble() / total
                        val dashLen = pct * circumference
                        val dashGap = circumference - dashLen
                        append("<circle cx=\"50\" cy=\"50\" r=\"40\" fill=\"none\" stroke=\"${seg.color}\" stroke-width=\"16\" ")
                        append("stroke-dasharray=\"$dashLen $dashGap\" ")
                        append("stroke-dashoffset=\"${-offset}\" ")
                        append("transform=\"rotate(-90 50 50)\"/>")
                        offset += dashLen
                    }
                    append("<circle cx=\"50\" cy=\"50\" r=\"32\" fill=\"rgba(10, 10, 30, 0.95)\"/>")
                    append("<text x=\"50\" y=\"46\" text-anchor=\"middle\" fill=\"white\" font-size=\"14\" font-weight=\"600\">$total</text>")
                    append("<text x=\"50\" y=\"58\" text-anchor=\"middle\" fill=\"rgba(255,255,255,0.5)\" font-size=\"7\">${t.dashboardDonutTotal}</text>")
                    append("</svg>")
                }

                key(svgContent) {
                    Div(
                        attrs = {
                            ref { el -> el.innerHTML = svgContent; onDispose { } }
                            style {
                                property("width", "100%")
                                property("height", "100%")
                            }
                        }
                    )
                }
                // Figma: inner shadow on pie chart area
                Box(
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border-radius", "inherit")
                        property("box-shadow", "inset 0px 3px 4px 0px rgba(0,0,0,0.2)")
                        property("pointer-events", "none")
                    }
                )
            }
        }

        // Legend — Figma: flex-col gap-12 items-start w-full
        Div(attrs = {
            style {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", s(12))
                property("align-items", "flex-start")
                width(100.percent)
                property("flex-shrink", "0")
            }
        }) {
            // Row 1: Confirmed, Completed, Pending — gap-24, left-aligned
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(24))
                    property("align-items", "center")
                    width(100.percent)
                    property("flex-shrink", "0")
                }
            }) {
                segments.take(3).forEach { seg ->
                    LegendItem(seg, total)
                }
            }
            // Row 2: Unpaid, Declined — Figma: justify-between, w-330
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "space-between")
                    property("width", s(330))
                    property("flex-shrink", "0")
                }
            }) {
                segments.drop(3).forEach { seg ->
                    LegendItem(seg, total)
                }
            }
        }
    }
}

@Composable
private fun LegendItem(seg: DonutSegment, total: Int) {
    val pct = (seg.count.toDouble() / total * 100).toInt()
    // Figma: flex gap-8 items-center shrink-0
    Div(attrs = {
        style {
            property("display", "flex")
            property("gap", s(8))
            property("align-items", "center")
            property("flex-shrink", "0")
        }
    }) {
        // Color square — Figma: 20x20, rounded-4
        Div(attrs = {
            style {
                property("width", s(20))
                property("height", s(20))
                property("border-radius", s(4))
                property("background", seg.color)
                property("flex-shrink", "0")
            }
        })
        // Label text — Figma: 16px Medium, line-height 18px, tracking 0.032px
        SpanText(
            seg.label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .color(AppColors.Text.Secondary)
                .styleModifier {
                    property("white-space", "nowrap")
                    property("line-height", s(18))
                    property("letter-spacing", "0.032px")
                    property("flex-shrink", "0")
                }
        )
        // Count wrapper — Figma: flex gap-2 items-center, 16px Medium
        Div(attrs = {
            style {
                property("display", "flex")
                property("gap", s(2))
                property("align-items", "center")
                property("flex-shrink", "0")
                property("white-space", "nowrap")
            }
        }) {
            SpanText(
                "${seg.count}",
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("line-height", s(18))
                        property("letter-spacing", "0.032px")
                    }
            )
            SpanText(
                "-",
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("line-height", s(18))
                        property("letter-spacing", "0.032px")
                    }
            )
            SpanText(
                "$pct%",
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("line-height", s(18))
                        property("letter-spacing", "0.032px")
                    }
            )
        }
    }
}

// =============================================================================
// Data point SVG radial gradients (from Figma)
// =============================================================================

private val revenueDotGradient = buildString {
    append("<svg viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>")
    append("<rect x='0' y='0' height='100%' width='100%' fill='url(#revDotGrad)' opacity='1'/>")
    append("<defs><radialGradient id='revDotGrad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' ")
    append("gradientTransform='matrix(0.32326 1.3622 -1.3622 0.32326 13.991 13.999)'>")
    append("<stop stop-color='rgba(0,97,255,1)' offset='0'/>")
    append("<stop stop-color='rgba(6,60,195,0.75)' offset='0.5'/>")
    append("<stop stop-color='rgba(12,23,135,0.5)' offset='1'/>")
    append("</radialGradient></defs></svg>")
}

private val bookingsDotGradient = buildString {
    append("<svg viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>")
    append("<rect x='0' y='0' height='100%' width='100%' fill='url(#bookDotGrad)' opacity='1'/>")
    append("<defs><radialGradient id='bookDotGrad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' ")
    append("gradientTransform='matrix(0.32326 1.3622 -1.3622 0.32326 13.991 13.999)'>")
    append("<stop stop-color='rgba(123,250,111,1)' offset='0'/>")
    append("<stop stop-color='rgba(96,218,84,0.875)' offset='0.25'/>")
    append("<stop stop-color='rgba(68,186,57,0.75)' offset='0.5'/>")
    append("<stop stop-color='rgba(41,153,30,0.625)' offset='0.75'/>")
    append("<stop stop-color='rgba(13,121,3,0.5)' offset='1'/>")
    append("</radialGradient></defs></svg>")
}

// =============================================================================
// Individual chart blocks
// =============================================================================

@Composable
fun RevenueBlock(
    chartData: Map<String, com.arena_web.data.dto.DashboardChartDto> = emptyMap(),
    onRequestPeriod: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    var period by remember { mutableStateOf("WEEK") }
    val chart = chartData[period]

    ChartCard(
        title = t.dashboardRevenueTitle, modifier = modifier,
        selectedPeriod = period,
        onPeriodChange = { period = it; onRequestPeriod(it) }
    ) {
        key(period) {
            if (chart != null) {
                val points = chart.revenuePoints
                val data = points.map { it.value }
                    .let { if (it.all { v -> v == 0.0 }) List(it.size.coerceAtLeast(1)) { 1.0 } else it }
                val labels = points.map { it.label }
                AreaChart(
                    data = data,
                    labels = labels,
                    lineColorHex = "#0c1787",
                    fillR = 0, fillG = 97, fillB = 255,
                    dotGradientSvg = revenueDotGradient,
                    idPrefix = "revenue-$period",
                    tooltipPrefix = "$ "
                )
            }
        }
    }
}

@Composable
fun BookingsBlock(
    chartData: Map<String, com.arena_web.data.dto.DashboardChartDto> = emptyMap(),
    onRequestPeriod: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    var period by remember { mutableStateOf("WEEK") }
    val chart = chartData[period]

    ChartCard(
        title = t.dashboardBookingsTitle, modifier = modifier,
        selectedPeriod = period,
        onPeriodChange = { period = it; onRequestPeriod(it) }
    ) {
        key(period) {
            if (chart != null) {
                val points = chart.bookingPoints
                val data = points.map { it.value }
                    .let { if (it.all { v -> v == 0.0 }) List(it.size.coerceAtLeast(1)) { 1.0 } else it }
                val labels = points.map { it.label }
                AreaChart(
                    data = data,
                    labels = labels,
                    lineColorHex = "#0d7903",
                    fillR = 13, fillG = 121, fillB = 3,
                    dotGradientSvg = bookingsDotGradient,
                    idPrefix = "bookings-$period",
                    tooltipPrefix = t.dashboardBookingsTooltipPrefix
                )
            }
        }
    }
}

@Composable
fun BookingStatusBlock(
    chartData: Map<String, com.arena_web.data.dto.DashboardChartDto> = emptyMap(),
    onRequestPeriod: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    var period by remember { mutableStateOf("WEEK") }
    val chart = chartData[period]

    ChartCard(
        title = t.dashboardBookingStatusTitle, modifier = modifier,
        selectedPeriod = period,
        onPeriodChange = { period = it; onRequestPeriod(it) }
    ) {
        key(period) {
            if (chart != null) {
                val statusDistribution = chart.statusDistribution
                val segments = listOf(
                    DonutSegment(t.dashboardStatusConfirmed, statusDistribution["CONFIRMED"] ?: 0, "#0c1787"),
                    DonutSegment(t.dashboardStatusCompleted, statusDistribution["COMPLETED"] ?: 0, "#0d7903"),
                    DonutSegment(t.dashboardStatusPending, statusDistribution["PENDING"] ?: 0, "#c6590c"),
                    DonutSegment(t.dashboardStatusUnpaid, statusDistribution["UNPAID"] ?: 0, "#aea725"),
                    DonutSegment(t.dashboardStatusDeclined, statusDistribution["DECLINED"] ?: 0, "#760606")
                ).let { list ->
                    if (list.all { it.count == 0 }) listOf(DonutSegment(t.dashboardNoData, 1, "#333")) else list
                }
                DonutChart(segments = segments)
            }
        }
    }
}

@Composable
fun BookingHeatmapBlock(
    chartData: Map<String, com.arena_web.data.dto.DashboardChartDto> = emptyMap(),
    totalFields: Int = 1,
    onRequestPeriod: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    var period by remember { mutableStateOf("WEEK") }
    val chart = chartData[period]
    val weekDays = listOf(
        t.dashboardDayMon, t.dashboardDayTue, t.dashboardDayWed, t.dashboardDayThu,
        t.dashboardDayFri, t.dashboardDaySat, t.dashboardDaySun,
    )
    val monthLabels = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    // For YEAR/MONTH: use labels from the chart's revenuePoints (months or day-buckets)
    val rowLabels = if (period != "WEEK" && chart != null) {
        chart.revenuePoints.map { it.label }
    } else weekDays
    val hours = (7..23).toList()
    val fields = totalFields.coerceAtLeast(1)

    val heatmapData: List<List<OccupancyLevel>>? = if (chart != null && chart.heatmap.isNotEmpty()) {
        chart.heatmap.map { row ->
            row.map { count ->
                val ratio = count.toDouble() / fields
                when {
                    ratio <= 0.0 -> OccupancyLevel.Empty
                    ratio <= 0.25 -> OccupancyLevel.Low
                    ratio <= 0.5 -> OccupancyLevel.Medium
                    ratio <= 0.75 -> OccupancyLevel.High
                    else -> OccupancyLevel.Full
                }
            }
        }
    } else null

    var hoveredCell by remember { mutableStateOf<Pair<Int, Int>?>(null) }

    ChartCard(
        title = t.dashboardBookingHeatmapTitle, modifier = modifier, paddingBottom = 28,
        selectedPeriod = period,
        onPeriodChange = { period = it; onRequestPeriod(it) }
    ) {
        key(period) {
        if (heatmapData != null) {
            // Main container — fills available space
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("flex", "1 1 0px")
                    property("min-height", "0")
                    width(100.percent)
                }
            }) {
                // Hour labels row
                Div(attrs = {
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "${s(36)} 1fr")
                        property("gap", s(4))
                        property("padding-top", s(12))
                        property("padding-bottom", s(2))
                        property("flex-shrink", "0")
                    }
                }) {
                    Div(attrs = {})
                    Div(attrs = {
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(${hours.size}, 1fr)")
                            property("gap", s(4))
                        }
                    }) {
                        hours.forEach { hour ->
                            SpanText(
                                t.dashboardHourLabel(hour),
                                modifier = Modifier
                                    .sFontSize(10)
                                    .fontWeight(AppTypography.Regular)
                                    .color(AppColors.Text.Secondary)
                                    .styleModifier {
                                        property("text-align", "center")
                                        property("white-space", "nowrap")
                                    }
                            )
                        }
                    }
                }

                // Heatmap grid — rows adapt to period
                val gridGap = if (rowLabels.size > 7) s(2) else s(4)
                Div(attrs = {
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "${s(36)} repeat(${hours.size}, 1fr)")
                        property("grid-template-rows", "repeat(${rowLabels.size}, 1fr)")
                        property("gap", gridGap)
                        property("flex", "1 1 0px")
                        property("min-height", "0")
                    }
                }) {
                    heatmapData.forEachIndexed { dayIdx, row ->
                        // Row label (day or month)
                        SpanText(
                            rowLabels.getOrElse(dayIdx) { "" },
                            modifier = Modifier
                                .sFontSize(12)
                                .fontWeight(AppTypography.Regular)
                                .color(AppColors.Text.Secondary)
                                .styleModifier {
                                    property("white-space", "nowrap")
                                    property("display", "flex")
                                    property("align-items", "center")
                                }
                        )
                        // 17 cells for this day
                        row.forEachIndexed { hourIdx, level ->
                            val isHovered = hoveredCell == (dayIdx to hourIdx)
                            val bookingCount = chart?.heatmap?.getOrNull(dayIdx)?.getOrNull(hourIdx) ?: 0
                            val hour = hours[hourIdx]

                            Div(attrs = {
                                onMouseEnter { hoveredCell = dayIdx to hourIdx }
                                onMouseLeave { hoveredCell = null }
                                style {
                                    property("border-radius", s(4))
                                    property("position", "relative")
                                    property("cursor", "pointer")
                                    property("transition", "transform 0.15s ease")
                                    if (isHovered) {
                                        property("transform", "scale(1.15)")
                                        property("z-index", "2")
                                        property("box-shadow", "0 0 ${s(6)} rgba(0, 97, 255, 0.5)")
                                    }
                                    property("background-color", when (level) {
                                        OccupancyLevel.Empty -> "#00143b"
                                        OccupancyLevel.Low -> "#00245d"
                                        OccupancyLevel.Medium -> "#0a2e67"
                                        OccupancyLevel.High -> "#004390"
                                        OccupancyLevel.Full -> "#0064b1"
                                    })
                                }
                            }) {
                                // Tooltip
                                if (isHovered) {
                                    Div(attrs = {
                                        style {
                                            property("position", "absolute")
                                            property("bottom", "calc(100% + ${s(6)})")
                                            property("left", "50%")
                                            property("transform", "translateX(-50%)")
                                            property("z-index", "10")
                                            property("pointer-events", "none")
                                            property("white-space", "nowrap")
                                            property("display", "flex")
                                            property("flex-direction", "column")
                                            property("align-items", "center")
                                        }
                                    }) {
                                        // Pill
                                        Div(attrs = {
                                            style {
                                                property("background", "#112140")
                                                property("border-radius", s(16))
                                                property("padding", "${s(4)} ${s(10)}")
                                            }
                                        }) {
                                            SpanText(
                                                t.dashboardHeatmapTooltip(
                                                    rowLabels.getOrElse(dayIdx) { "" },
                                                    hour,
                                                    hour + 1,
                                                    bookingCount,
                                                ),
                                                modifier = Modifier
                                                    .sFontSize(12)
                                                    .fontWeight(AppTypography.Regular)
                                                    .color(AppColors.Text.Secondary)
                                            )
                                        }
                                        // Triangle
                                        Div(attrs = {
                                            style {
                                                property("width", "0")
                                                property("height", "0")
                                                property("border-left", "${s(4)} solid transparent")
                                                property("border-right", "${s(4)} solid transparent")
                                                property("border-top", "${s(5)} solid #112140")
                                                property("margin-top", "-1px")
                                            }
                                        })
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        } // key(period)
    }
}
